import { getSupabase } from "../auth/supabaseClient";
import type { LeaderboardEntry, SoloMode } from "../game/types";

export async function fetchLeaderboard(limit = 100, mode: SoloMode = "classic"): Promise<LeaderboardEntry[]> {
  try {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from("scores")
      .select("display_name, score, level, created_at")
      .eq("solo_mode", mode)
      .order("score", { ascending: false })
      .limit(limit);
    if (error || data == null) {
      return [];
    }
    return data
      .map((row) => ({
        name: row.display_name,
        score: row.score,
        level: row.level,
        createdAt: new Date(row.created_at).getTime()
      }))
      .sort((a, b) => (b.score - a.score) || (b.createdAt - a.createdAt));
  } catch {
    return [];
  }
}

export async function fetchScoreRank(score: number, mode: SoloMode = "classic"): Promise<number | null> {
  try {
    const supabase = getSupabase();
    const { count, error } = await supabase
      .from("scores")
      .select("*", { count: "exact", head: true })
      .eq("solo_mode", mode)
      .gt("score", score);
    if (error || count == null) return null;
    return count + 1;
  } catch {
    return null;
  }
}

export interface PersonalBestEntry { score: number; rank: number | null }
export type PersonalBests = Partial<Record<SoloMode, PersonalBestEntry>>;

export async function fetchPersonalBests(userId: string): Promise<PersonalBests> {
  try {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from("scores")
      .select("solo_mode, score")
      .eq("user_id", userId)
      .order("score", { ascending: false });
    if (error || data == null) return {};
    // Find best score per mode
    const bestScores: Partial<Record<SoloMode, number>> = {};
    for (const row of data) {
      const mode = row.solo_mode as SoloMode;
      if (bestScores[mode] == null || row.score > bestScores[mode]!) {
        bestScores[mode] = row.score;
      }
    }
    // Fetch rank for each best score in parallel
    const modes = Object.keys(bestScores) as SoloMode[];
    const ranks = await Promise.all(
      modes.map((mode) => fetchScoreRank(bestScores[mode]!, mode))
    );
    const bests: PersonalBests = {};
    modes.forEach((mode, i) => {
      bests[mode] = { score: bestScores[mode]!, rank: ranks[i] };
    });
    return bests;
  } catch {
    return {};
  }
}

export type SubmitScoreResult = "ok" | "duplicate" | "error";

/** Set of idempotency keys for scores already submitted this session. */
const submittedScoreKeys = new Set<string>();

/** Build a dedup key from the run's identifying data. */
export function buildScoreKey(userId: string, mode: SoloMode, score: number, level: number): string {
  return `${userId}_${mode}_${score}_${level}`;
}

export async function submitScore(
  userId: string,
  displayName: string,
  score: number,
  level: number,
  mode: SoloMode = "classic"
): Promise<SubmitScoreResult> {
  const normalizedScore = Math.max(0, Math.floor(score));
  const normalizedLevel = Math.max(1, Math.floor(level));
  const normalizedName = (displayName || "PLAYER").slice(0, 24);

  const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId);
  if (!looksLikeUuid) {
    return "error";
  }

  const key = buildScoreKey(userId, mode, normalizedScore, normalizedLevel);
  if (submittedScoreKeys.has(key)) {
    return "duplicate";
  }

  try {
    const supabase = getSupabase();
    const { error } = await supabase
      .from("scores")
      .insert({
        user_id: userId,
        display_name: normalizedName,
        score: normalizedScore,
        level: normalizedLevel,
        solo_mode: mode
      });
    if (error != null) {
      return "error";
    }
    submittedScoreKeys.add(key);
    return "ok";
  } catch {
    return "error";
  }
}
